import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  LANGS,
  DEFAULT_LANG,
  parsePath,
  localizePath,
  LANG_HREFLANG,
  LANG_OG_LOCALE,
} from "@/i18n/locales";

interface SEOHeadProps {
  title: string;
  description: string;
  /**
   * Optional explicit canonical override. Normally omitted: the canonical is
   * derived from the current route + active language so each locale is
   * self-referential. Only pass this for routes that must point elsewhere.
   */
  canonical?: string;
  /** Optional structured data (JSON-LD) injected for this route. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Optional per-route social image (absolute URL). */
  ogImage?: string;
  ogType?: string;
  /** When true, emit robots noindex for this route (e.g. the 404 page). */
  noindex?: boolean;
}

const BASE_URL = "https://devcore.systems";
const DEFAULT_TITLE = "DevCore | Custom SaaS, Portals & Software Development";
const INDEXABLE_ROBOTS = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

const SEOHead = ({ title, description, canonical, jsonLd, ogImage, ogType, noindex }: SEOHeadProps) => {
  const location = useLocation();

  useEffect(() => {
    document.title = title;

    const { lang, path } = parsePath(location.pathname);
    // Always self-reference the active locale so each language canonicalises to
    // itself. An explicit `canonical` only overrides the English/default route.
    const derived = `${BASE_URL}${localizePath(path, lang)}`;
    const resolvedCanonical = lang === DEFAULT_LANG && canonical ? canonical : derived;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "robots", noindex ? "noindex, follow" : INDEXABLE_ROBOTS);
    setMeta("property", "og:type", ogType || "website");
    setMeta("property", "og:url", resolvedCanonical);
    setMeta("property", "og:locale", LANG_OG_LOCALE[lang]);
    if (ogImage) {
      setMeta("property", "og:image", ogImage);
      setMeta("name", "twitter:image", ogImage);
    }

    // og:locale:alternate for the other languages.
    const removeBy = (selector: string) =>
      document.querySelectorAll(selector).forEach((n) => n.parentNode?.removeChild(n));
    removeBy('meta[data-seo-alt-locale="1"]');
    LANGS.filter((l) => l !== lang).forEach((l) => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:locale:alternate");
      m.setAttribute("content", LANG_OG_LOCALE[l]);
      m.setAttribute("data-seo-alt-locale", "1");
      document.head.appendChild(m);
    });

    // Canonical (self-referential, localized).
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", resolvedCanonical);

    // hreflang alternates for every language + x-default (English).
    removeBy('link[data-seo-hreflang="1"]');
    const addAlternate = (hreflang: string, href: string) => {
      const a = document.createElement("link");
      a.setAttribute("rel", "alternate");
      a.setAttribute("hreflang", hreflang);
      a.setAttribute("href", href);
      a.setAttribute("data-seo-hreflang", "1");
      document.head.appendChild(a);
    };
    LANGS.forEach((l) => addAlternate(LANG_HREFLANG[l], `${BASE_URL}${localizePath(path, l)}`));
    addAlternate("x-default", `${BASE_URL}${localizePath(path, DEFAULT_LANG)}`);

    // Per-route JSON-LD structured data
    let scriptEl: HTMLScriptElement | null = null;
    if (jsonLd) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.setAttribute("data-seo-jsonld", "route");
      scriptEl.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(scriptEl);
    }

    return () => {
      document.title = DEFAULT_TITLE;
      setMeta("name", "robots", INDEXABLE_ROBOTS);
      if (scriptEl && scriptEl.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl);
      }
    };
  }, [title, description, canonical, jsonLd, ogImage, ogType, noindex, location.pathname]);

  return null;
};

export default SEOHead;
